import { t } from 'i18next';

import Button from 'coreOld/components/Button';
import DateInterval from 'coreOld/components/DateInterval';
import Field from 'coreOld/components/Field';
import { InputType, FilterField as Filter, Size } from 'coreOld/enums';
import { IFilters } from 'coreOld/types';
import AddCompanyPart from 'partsOld/components/AddCompanyPart';
import Import from 'partsOld/components/Import';

import AdvancedFilter from './AdvancedFilter';
import CheckboxGroup from './CheckboxGroup';
import DateRange from './DateRange';
import { ButtonWrapper, textFieldStyle } from './filtersStyle';
import InputGroup from './InputGroup';
import RangeFilter from './RangeFilter';
import SearchFilter from './SearchFilter';

interface IProps extends IFilters {
  onChangeAdvancedFilter?: (name: string, value: number | string, clearFilters?: boolean) => void;
  onApplyAdvancedFilters?: () => void;
  filter: Record<string, any>;
}

const getFilterGroupValues = (filter: Record<string, any>, values: any) => {
  if (!filter?.individualFields) {
    return values[filter.name];
  }

  let internValues = {};
  filter.fields.forEach((field: Record<string, any>) => {
    if (!values[field.name]) {
      return;
    }

    internValues = {
      ...internValues,
      [field.name]: values[field.name],
    };
  });

  return internValues;
};

const FilterField = ({
  onChangeAdvancedFilter,
  onApplyAdvancedFilters,
  onChangeFilter,
  filter,
  values,
  advancedFiltersValues,
  onAdd,
  advancedFiltersConfig,
}: IProps) => {
  switch (filter.type) {
    case Filter.TEXTFIELD:
      return (
        <Field
          type={InputType.TEXTFIELD}
          name={filter.name}
          inputProps={{
            onChange: (e) => onChangeFilter(filter.name, e.target.value),
            size: Size.MD,
            style: textFieldStyle,
            value: values?.[filter.name] || '',
            ...filter.props,
          }}
        />
      );
    case Filter.SEARCH:
      return (
        <SearchFilter
          onChangeFilter={onChangeFilter}
          filter={filter}
          value={values?.[filter.name] || ''}
          {...filter.props}
        />
      );
    case Filter.DATE:
      return (
        <DateInterval
          onChange={(value) => onChangeFilter(filter.name, value)}
          name={filter.name}
          value={values[filter.name]}
          {...filter.props}
        />
      );
    case Filter.SELECT:
      return (
        <Field
          type={InputType.SELECT}
          name={filter.name}
          inputProps={{
            onChange: (value) => onChangeFilter(filter.name, value),
            defaultValue: values?.[filter?.name] || null,
            ...filter.props,
          }}
        />
      );
    case Filter.INPUT_GROUP:
      return (
        <InputGroup
          onChange={(value) => onChangeFilter(filter.name, value)}
          name={filter.name}
          filter={filter}
          fields={filter.fields}
          values={getFilterGroupValues(filter, values)}
        />
      );
    case Filter.AUTOCOMPLETE:
      return (
        <Field
          type={InputType.ASYNC_SELECT}
          name={filter.name}
          inputProps={{
            onChange: (value) => onChangeFilter(filter.name, value),
            value: values?.[filter?.name] || null,
            ...filter.props,
          }}
        />
      );
    case Filter.RANGE:
      return (
        <RangeFilter
          onChange={(value) => onChangeFilter(filter.name, value)}
          name={filter.name}
          filter={filter}
          checkboxProps={filter.checkbox}
          value={values[filter.name] || null}
        />
      );
    case Filter.DATE_RANGE:
      return (
        <DateRange
          onChange={(value) => onChangeFilter(filter.name, value)}
          name={filter.name}
          filter={filter}
          value={values[filter.name]}
        />
      );
    case Filter.CHECKBOX_GROUP:
      return (
        <CheckboxGroup
          onChange={(value) => onChangeFilter(filter.name, value)}
          name={filter.name}
          filter={filter}
          checkboxes={filter.checkboxes}
          values={values[filter.name]}
          isMulti={filter.isMulti}
        />
      );
    case Filter.ADVANCED:
      return (
        <AdvancedFilter
          values={{ ...values, ...advancedFiltersValues }}
          customPanelStyle={filter?.panelStyle}
          advancedFiltersConfig={advancedFiltersConfig}
          advancedFiltersValues={advancedFiltersValues}
          onChangeAdvancedFilter={onChangeAdvancedFilter}
          onApplyAdvancedFilters={onApplyAdvancedFilters}
        />
      );
    case Filter.ADD_PART:
      return <AddCompanyPart filter={filter} />;
    case Filter.ADD:
      return (
        <ButtonWrapper>
          <Button id={filter?.id} onClick={onAdd} fullHeight>
            {filter?.label || t('buttonLabelAdd')}
          </Button>
        </ButtonWrapper>
      );
    case Filter.IMPORT:
      return <Import />;
    default:
      throw new Error(`Unknown filter type "${filter.type}"`);
  }
};

export default FilterField;
